// +----------------------------------------------------------------------
// | 领码页 /free-code
// |
// | 场景：公众号文章里给一句暗号，用户看完文章回来输这两个字，领一张体验码。
// |
// | 三个状态要分清（页面只在这三种里切，不要有第四种）：
// |   ① 没领过 → 输入框 + 「领取」
// |   ② 领过了 → 直接把码大字摆出来 + 复制按钮 + 「去兑换」
// |   ③ 领过且已核销 → 显示会员已开通，不再给码（避免用户以为还能再领）
// |
// | 为什么不做成"关注即自动发"：
// |   ① 关注事件虽然能触发客服接口，但额度只有「1 分钟 / 3 条」（2026 现行规则），
// |      不是 48 小时；48 小时窗口只给「用户主动发消息」；
// |   ② 客服接口需要「认证服务号」，本项目是个人主体订阅号，无法认证 —— 权限就没有；
// |   ③ 个人号菜单连外链都挂不了（非认证订阅号只能跳素材库）。
// |   所以公众号侧只能"引导"，真正的发码必须落在产品内：用户输暗号，我们发码。
// |   等将来升级成企业主体认证服务号，再考虑接事件回调自动发。
// |
// | 做成沉浸子页（手机端藏顶栏/底 Tab，页面自己带「←」返回）：
// |   公众号「阅读原文」落地的是主页面（首页），领码页只是从首页/我的
// |   点进来的二级页 —— 不承担品牌门面，藏掉顶栏更聚焦「输暗号」这一件事。
// |   布局走 .layout--imm（普通滚动），不用 .layout--chat 的锁视口
// |   —— 锁视口是聊天底部输入框的刚需，这页用不上。
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getClaimStatus, claimByKeyword } from '../../api/claim';
import { toast } from '../../components/Toast';
import './FreeCode.less';

export default function FreeCode() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // 返回逻辑已上收到 Layout 的子页返回钮（location.key==='default' 时回首页）

  const load = useCallback(async () => {
    try {
      const data = await getClaimStatus();
      setStatus(data);
    } catch (e) {
      toast(e.message || '没拉到领取状态', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClaim = async (e) => {
    e.preventDefault();
    const value = keyword.trim();
    if (!value) {
      toast('请输入暗号', 'error');
      return;
    }
    if (submitting) return;

    setSubmitting(true);
    try {
      const data = await claimByKeyword(value);
      setKeyword('');
      setStatus((prev) => ({
        ...(prev || {}),
        claimed: true,
        code: data.code,
        used: false,
        days: data.days,
        product_name: data.product_name,
        remaining: data.remaining,
      }));
      toast(data.first_time ? '领到了' : '你之前领过的码在这儿', 'success');
    } catch (err) {
      toast(err.message || '没领到，再试一次', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // 降级复制：clipboard API 被拒/不可用时的兜底。
  // 关键细节：
  //   ① textarea 要 focus() + setSelectionRange —— iOS Safari 和部分安卓壳
  //      只认显式选区，光 select() 经常返回 true 但剪贴板是空的；
  //   ② fixed + 移出屏幕外（top:-9999px），别用 display:none（选不中）；
  //   ③ execCommand 虽然废弃但在 WebView 里是唯一能用的同步通道。
  const legacyCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
      if (typeof ta.setSelectionRange === 'function') {
        ta.setSelectionRange(0, text.length);
      }
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  };

  const copyCode = async () => {
    const code = status && status.code;
    if (!code) return;

    // 第一层：现代 Clipboard API。注意它在很多安卓壳/WebView 里「存在但被拒」，
    // writeText 会 reject —— 不能直接进 catch 报错就完事，必须再试第二层，
    // 否则用户看到的就是「点了没反应」（clipboard 挂了、降级又没跑到）。
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        ok = true;
      }
    } catch (e) {
      ok = false;
    }

    // 第二层：textarea + execCommand 兜底
    if (!ok) ok = legacyCopy(code);

    if (ok) {
      setCopied(true);
      toast('码已复制', 'success');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast('复制没成功，长按那串码手动复制吧', 'error');
    }
  };

  if (loading) {
    return <div className="page center muted">正在看领取状态…</div>;
  }

  // 顶栏（返回钮 + 居中标题「领取体验码」）由 Layout 统一提供（2026-09-29
  // 子页顶栏大统一），页面里不再自绘 titleBar。

  // 通道关闭 / 后端未就绪
  if (!status || !status.enabled || !status.ready) {
    return (
      <div className="page freecode">
        <div className="card freecode-closed">
          <strong>领取通道暂时关着</strong>
          <p className="muted">
            {status && status.hint ? status.hint : '过段时间再来看看，或者去公众号里问问。'}
          </p>
        </div>
      </div>
    );
  }

  const remaining = Number(status.remaining || 0);
  const soldOut = !status.claimed && remaining <= 0;

  return (
    <div className="page freecode">
      {status.hint && <p className="freecode-hint">{status.hint}</p>}

      {/* ---------- 关注哪个公众号：光说"去公众号"不说是哪个，用户没法关注 ---------- */}
      {(status.gzh_name || status.gzh_qrcode) && (
        <div className="freecode-gzh">
          {status.gzh_qrcode && (
            <img className="freecode-gzh-qr" src={status.gzh_qrcode} alt="公众号二维码" />
          )}
          <div className="freecode-gzh-text">
            <strong>没有暗号？关注公众号「{status.gzh_name}」</strong>
            <p>
              {status.gzh_qrcode
                ? '扫码关注；在微信里打不开扫码，就搜索这个名字。暗号在文章里发。'
                : '在微信里搜索这个名字关注，暗号在它的文章里发。'}
            </p>
          </div>
        </div>
      )}

      {/* ---------- 已领取：把码摆出来 ---------- */}
      {status.claimed && (
        <div className="card freecode-card freecode-card--got">
          <span className="freecode-label">
            {status.used ? '你领的这张已经用掉了' : '你的体验码'}
          </span>

          {!status.used && (
            <>
              <button type="button" className="freecode-code" onClick={copyCode}>
                {status.code}
              </button>
              <span className="freecode-code-tip muted">
                {copied ? '已复制 ✓' : '↑ 轻触上面的码，一键复制'}
              </span>
            </>
          )}

          {status.used ? (
            <p className="freecode-desc">
              就是这张，已经开通成功了。会员状态去「我的」页看。
            </p>
          ) : (
            <p className="freecode-desc">
              {status.product_name || `会员 ${status.days} 天`}。拿着这串码去「我的」页兑换，兑换完立刻生效。
            </p>
          )}

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => navigate('/profile')}
          >
            {status.used ? '去看我的会员' : '去兑换'}
          </button>

          <p className="freecode-note muted">一个账号只能领一张，领过就找不回来第二张了。</p>
        </div>
      )}

      {/* ---------- 没领过：输暗号 ---------- */}
      {!status.claimed && !soldOut && (
        <form className="card freecode-card freecode-card--claim" onSubmit={handleClaim}>
          <label className="freecode-label" htmlFor="keyword">输入公众号里的暗号</label>
          <div className="freecode-form">
            <input
              id="keyword"
              className="input"
              type="text"
              value={keyword}
              maxLength={32}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="两个字就够了"
            />
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '领取中…' : '领取'}
            </button>
          </div>
          <p className="freecode-note muted">
            大小写、空格都不讲究，输错了会告诉你。
          </p>
        </form>
      )}

      {/* ---------- 发完了 ---------- */}
      {!status.claimed && soldOut && (
        <div className="card freecode-card freecode-card--empty">
          <strong>这批发完了</strong>
          <p className="muted">码是限量的，下一批会在公众号里说。先去用用免费的每日额度？</p>
          <Link className="btn btn-primary btn-block" to="/">回首页</Link>
        </div>
      )}

      {/* 库存提示：手慢无的紧迫感，也让用户知道不是无底洞 */}
      {remaining > 0 && (
        <p className="freecode-stock muted">还剩 {remaining} 张</p>
      )}
    </div>
  );
}
